/* eslint-disable import/prefer-default-export */
import { FormattedMessage } from 'react-intl';
import React from 'react';
import camelCase from 'lodash/camelCase';
import isFinite from 'lodash/isFinite';
import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import messages from './messages';
import './styles.css';
import { numericFields } from './constants';
import GenericModal from '../../components/GenericModal';

const valueIsEmpty = (value) => (value === undefined || String(value) === '');
const removeCommas = (value) => (value ? value.toString().replace(',', '') : value);
const addCommas = (value) => (!isNil(value) ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : value);

const isUsableValue = (value) => {
  /* istanbul ignore if */
  if (value === '') return false;
  const numericValue = Number(removeCommas(value));
  return numericValue > 0 && isFinite(numericValue);
};

const bothGpaAndClassRank = (gpaMin, classRankMax) => gpaMin && classRankMax;
const eitherGpaOrClassRank = (gpaMin, classRankMax) => gpaMin || classRankMax;
const bothSatAndAct = (satMin, actMin) => satMin && actMin;
const eitherSatOrAct = (satMin, actMin) => satMin || actMin;

// eslint-disable-next-line max-len
const eitherTopOrBottom = (gpaMin, classRankMax, satMin, actMin) => eitherGpaOrClassRank(gpaMin, classRankMax) && eitherSatOrAct(satMin, actMin);

const eitherNeededForGpaAndClassRank = (gpaRankMatch, gpaMin, classRankMax) =>
  gpaRankMatch === 'OR' && bothGpaAndClassRank(gpaMin, classRankMax);

const eitherNeededForSatAndAct = (satActMatch, satMin, actMin) =>
  satActMatch === 'OR' && bothSatAndAct(satMin, actMin);

const eitherNeededForTopAndBottom = (satGpaMatch, gpaMin, classRankMax, satMin, actMin) =>
  satGpaMatch === 'OR' && eitherTopOrBottom(gpaMin, classRankMax, satMin, actMin);

/**
 * Determine where to place the word 'either' in the summary message
 *   Place the word 'either' before the Gpa/ClassRank if either:
 *     1. both gpa and classRank exist, and the 'AND/OR' switch between them is set to 'OR'
 *     2. either gpa or classRank exist and either sat or act exist, and the 'AND/OR' switch between the Gpa/ClassRank and sat/Act = 'OR'
 *   Place the word 'either' before the Sat/Act if both the sat and act exist, and the 'AND/OR' switch between them is set to 'OR'
 * @param gpaMin
 * @param classRankMax
 * @param satMin
 * @param actMin
 * @param gpaRankMatch
 * @param satGpaMatch
 * @param satActMatch
 * @returns {*[]}
 */
const getEitherTags = (gpaMin, classRankMax, satMin, actMin, gpaRankMatch, satGpaMatch, satActMatch) => [
  (eitherNeededForGpaAndClassRank(gpaRankMatch, gpaMin, classRankMax) || eitherNeededForTopAndBottom(satGpaMatch, gpaMin, classRankMax, satMin, actMin)) ? 'either ' : '',
  eitherNeededForSatAndAct(satActMatch, satMin, actMin) ? 'either ' : '',
];

const getAndOrTags = (gpaMin, classRankMax, satMin, actMin, gpaRankMatch, satGpaMatch, satActMatch) => [
  bothGpaAndClassRank(gpaMin, classRankMax) ? ` ${gpaRankMatch} ` : '',
  eitherTopOrBottom(gpaMin, classRankMax, satMin, actMin) ? ` ${satGpaMatch} ` : '',
  bothSatAndAct(satMin, actMin) ? ` ${satActMatch} ` : '',
];

/*
  Construct the text for a given academic score
  the right side is only shown if both left and right exist
 */
const getScoreText = (left, right, prefix, suffix) => {
  let rightHalf = '';
  if (isUsableValue(right)) {
    rightHalf = isUsableValue(left) ? ` - ${right}` : '';
  }
  return `${prefix}${left}${rightHalf}${suffix}`;
};

const gpaSummary = (gpaMin, gpaMax, gpa) => {
  if (gpaMin) {
    return (
      <React.Fragment>
        <span>{isUsableValue(gpaMax) ? 'a ' : 'at least a '}</span>
        <span styleName="merit-scholarships-requirement-values">{gpa}</span>
      </React.Fragment>
    );
  } else {
    return null;
  }
};

const classRankSummary = (classRankMin, classRankMax, classRank) => {
  if (classRankMax) {
    return (
      <React.Fragment>
        <span>a </span>
        <span styleName="merit-scholarships-requirement-values">{classRank}</span>
      </React.Fragment>
    );
  } else {
    return null;
  }
};

const satSummary = (satMin, satMax, sat) => {
  if (satMin) {
    return (
      <React.Fragment>
        <span>{isUsableValue(satMax) ? 'a ' : 'at least a '}</span>
        <span styleName="merit-scholarships-requirement-values">{sat}</span>
      </React.Fragment>
    );
  } else {
    return null;
  }
};

const actSummary = (actMin, actMax, act) => {
  if (actMin) {
    return (
      <React.Fragment>
        <span>{isUsableValue(actMax) ? 'a ' : 'at least a '}</span>
        <span styleName="merit-scholarships-requirement-values">{act}</span>
      </React.Fragment>
    );
  } else {
    return null;
  }
};

export const requiredValuesExist =
  (values) => Object.keys(values).some((key) => values[key] !== undefined && String(values[key]) !== '' && Number(values[key]) > 0);

const getSummaryMessage = (
  gpaMin,
  gpaMax,
  classRankMin,
  classRankMax,
  satMin,
  satMax,
  actMin,
  actMax,
  gpaRankMatch,
  satGpaMatch,
  satActMatch,
) => {
  if (requiredValuesExist({ gpaMin, classRankMax, satMin, actMin })) {
    const [either1, either2] = getEitherTags(gpaMin, classRankMax, satMin, actMin, gpaRankMatch, satGpaMatch, satActMatch);
    const [gpaClassRankAndOr, satGpaAndOr, satActAndOr] =
      getAndOrTags(gpaMin, classRankMax, satMin, actMin, gpaRankMatch, satGpaMatch, satActMatch);

    const gpa = getScoreText(gpaMin, gpaMax, '', ' GPA');
    const classRank = getScoreText(classRankMax, classRankMin, 'Class Rank in the top ', '%');
    const sat = getScoreText(satMin, satMax, '', ' SAT');
    const act = getScoreText(actMin, actMax, '', ' ACT');

    return (
      <div data-cy="merit-scholarships-requirements-summary">
        <FormattedMessage {...messages.meritScholarshipsSummaryMessagePrefix} />
        <span>{either1}</span>
        {gpaSummary(gpaMin, gpaMax, gpa)}
        <span>{gpaClassRankAndOr}</span>
        {classRankSummary(classRankMin, classRankMax, classRank)}
        <span>{satGpaAndOr}</span>
        <span>{either2}</span>
        {satSummary(satMin, satMax, sat)}
        <span>{satActAndOr}</span>
        {actSummary(actMin, actMax, act)}
        <span>.</span>
      </div>
    );
  }
  return null;
};

const checkName = (requiredFieldsMissing, name) => {
  if (isNil(name) || name === '') {
    requiredFieldsMissing.push('name');
  }
};

const checkAwardAmount = (requiredFieldsMissing, awardType, awAmountMin, awAmountMax) => {
  if (awardType !== 'FULL_TUITION' && valueIsEmpty(awAmountMin)) {
    requiredFieldsMissing.push('awAmountMin');
  }
  if (awardType === 'RANGE' && valueIsEmpty(awAmountMax)) {
    requiredFieldsMissing.push('awAmountMax');
  }
};

const checkAudience = (requiredFieldsMissing, audience, audienceStates) => {
  if (audience === 'CUSTOM' && (isNil(audienceStates) || audienceStates.length === 0)) {
    requiredFieldsMissing.push('audienceStates');
  }
};

const checkRequiredFields = (scholarship) => {
  const requiredFieldsMissing = [];
  const { name, awardType, awAmountMin, awAmountMax, audience, audienceStates } = scholarship;
  checkName(requiredFieldsMissing, name);
  checkAwardAmount(requiredFieldsMissing, awardType, awAmountMin, awAmountMax);
  checkAudience(requiredFieldsMissing, audience, audienceStates);

  return requiredFieldsMissing;
};

const fixMinMaxValues = (scholarship) => {
  const {
    gpaMin,
    gpaMax,
    classRankMin,
    classRankMax,
    satMin,
    satMax,
    actMin,
    actMax,
  } = scholarship;
  if (!gpaMin && gpaMax) {
    scholarship.gpaMin = null;
    scholarship.gpaMax = null;
  }
  if (!satMin && satMax) {
    scholarship.satMin = null;
    scholarship.satMax = null;
  }
  if (!actMin && actMax) {
    scholarship.actMin = null;
    scholarship.actMax = null;
  }
  if (!classRankMax && classRankMin) {
    scholarship.classRankMax = null;
    scholarship.classRankMin = null;
  }
  return scholarship;
};

const fixAwardAmountsAndMinMaxValue = (scholarship) => {
  const { awardType } = scholarship;
  if (awardType !== 'RANGE') {
    scholarship.awAmountMax = 0;
  }
  if (awardType === 'FULL_TUITION') {
    scholarship.awAmountMin = 0;
  }
  return fixMinMaxValues(scholarship);
};

const fixNumericFields = (scholarship) => {
  numericFields.forEach((field) => {
    const value = removeCommas(scholarship[field]);
    /* istanbul ignore next */
    if (isFinite(value) || (typeof value === 'string' && isFinite(Number(value)))) {
      scholarship[field] = value === '' ? null : Number(value);
    }
  });
  return fixAwardAmountsAndMinMaxValue(scholarship);
};

const validationRegex = {
  'GPA': '^[0-9.]*$',
  'SAT': '^[0-9]*$',
  'ACT': '^[0-9]*$',
  'Class Rank': '^[0-9]*$',
  'Award Amount': '^[0-9,]*$',
};

const isValidValue = (value, scoreType) => {
  const numericValue = scoreType === 'Award Amount' ? removeCommas(String(value)) : value;
  if (scoreType === 'GPA' && typeof numericValue === 'string') {
    // do not allow more than 2 digits past the decimal point
    const indx = numericValue.indexOf('.');
    if (indx > -1 && indx < (numericValue.length - 3)) {
      return false;
    }
  }
  return RegExp(validationRegex[scoreType]).test(numericValue);
};

const valueExists = (value) => (!isNil(value) && String(value) !== '' && (isNaN(Number(value)) || Number(value) > 0));

const validateValues = (intl, minValue, maxValue, minAllowed, maxAllowed, scoreType) => {
  const min = scoreType === 'Award Amount' ? removeCommas(String(minValue)) : minValue;
  const max = scoreType === 'Award Amount' ? removeCommas(String(maxValue)) : maxValue;
  const score = ['ACT', 'SAT'].includes(scoreType) ? ' score' : '';
  const errors = {};
  if (valueExists(min)) {
    if (valueExists(max) && Number(min) >= Number(max)) {
      errors[`${camelCase(scoreType)}RangeError`] = intl.formatMessage(messages.invalidRange, { scoreType });
    }
    if (!isValidValue(min, scoreType) || Number(min) < minAllowed || Number(min) > maxAllowed) {
      errors[`${camelCase(scoreType)}MinError`] =
        intl.formatMessage(messages.invalidValue, { scoreType: `${scoreType}${score}`, minAllowed, maxAllowed });
    }
  }

  if (valueExists(max)) {
    if (!isValidValue(max, scoreType) || Number(max) < minAllowed || Number(max) > maxAllowed) {
      errors[`${camelCase(scoreType)}MaxError`] =
        intl.formatMessage(messages.invalidValue, { scoreType: `${scoreType}${score}`, minAllowed, maxAllowed });
    }
  }
  return Object.keys(errors).length > 0 ? errors : undefined;
};

const checkForErrors = (intl, scholarship) => {
  const {
    gpaMin,
    gpaMax,
    classRankMin,
    classRankMax,
    satMin,
    satMax,
    actMin,
    actMax,
  } = scholarship;
  const requiredFieldsMissing = checkRequiredFields(scholarship);
  const atLeast1Requirement = requiredValuesExist({ gpaMin, classRankMax, satMin, actMin });
  return {
    ...validateValues(intl, gpaMin, gpaMax, 0.01, 4.00, 'GPA'),
    ...validateValues(intl, classRankMin, classRankMax, 0, 100, 'Class Rank'),
    ...validateValues(intl, satMin, satMax, 400, 1600, 'SAT'),
    ...validateValues(intl, actMin, actMax, 1, 36, 'ACT'),
    requiredFieldsMissing,
    atLeast1Requirement: !atLeast1Requirement ? intl.formatMessage(messages.atLeast1Requirement) : undefined,
  };
};

const isEmptyObject = (obj) => Object.keys(obj).reduce((acc, key) => (acc && obj[key] === undefined), true);
const errorsExist = (errors) =>
  errors && (!isEmptyObject(omit(errors, 'requiredFieldsMissing')) || errors.requiredFieldsMissing.length > 0);

const getUnsavedModal = (intl, openSaveModal, onCancel, onConfirm) => (
  <React.Fragment>
    <GenericModal
      open={openSaveModal}
      title={intl.formatMessage(messages.unsavedHeader)}
      message={intl.formatMessage(messages.modalSave)}
      cancelText="DON'T SAVE CHANGES"
      confirmText="SAVE"
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  </React.Fragment>
);

const getCancelModal = (intl, openCancelModal, onCancel, onConfirm) => (
  <GenericModal
    open={openCancelModal}
    title={intl.formatMessage(messages.cancelHeader)}
    message={intl.formatMessage(messages.cancel)}
    cancelText="NO"
    confirmText="YES"
    onCancel={onCancel}
    onConfirm={onConfirm}
  />
);

const getDeleteModal = (intl, openDeleteModal, onCancel, onConfirm) => (
  <GenericModal
    open={openDeleteModal}
    title={intl.formatMessage(messages.deleteHeader)}
    message={intl.formatMessage(messages.delete)}
    cancelText="CANCEL"
    confirmText="DELETE"
    onCancel={onCancel}
    onConfirm={onConfirm}
  />
);

export {
  fixNumericFields,
  getSummaryMessage,
  checkRequiredFields,
  validateValues,
  checkForErrors,
  isEmptyObject,
  errorsExist,
  addCommas,
  removeCommas,
  isUsableValue,
  getCancelModal,
  getDeleteModal,
  getUnsavedModal,
};
/* eslint-enable import/prefer-default-export */
